import type React from 'react';
import { useEffect, useMemo } from 'react';
import { ProgramCard } from '@widgets/programs';
import { InfinityList } from '@widgets/shared/InfinityList/index.tsx';
import type { CachedListData } from '@shared/hooks/use-save-scroll.ts';
import type { ActiveTabType } from '@shared/types/common';
import { SkeletonList } from '@shared/ui/skeleton-list';
import { useProgramsInfiniteQuery } from '@src/api/api-hooks/programs/useProgramsInfiniteQuery.ts';
import { Blank } from 'library/components/common';
import * as st from './styles.module.scss';

interface ProgramListQueryProps {
  tabName?: ActiveTabType;
  params: Record<string, any>;
  setTitles?: (titles: string[]) => void;
  doSetStorageData: (data: CachedListData) => void;
}

export const ListQuery: React.FC<ProgramListQueryProps> = ({
  tabName,
  params,
  setTitles,
  doSetStorageData,
}) => {
  const {
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    data: programsData,
    fetchNextPage,
    isError,
  } = useProgramsInfiniteQuery({
    ...params,
    tabName: tabName ?? 'all',
  });

  const programsList = useMemo(() => programsData?.results || [], [programsData]);

  useEffect(() => {
    const titles = programsList.map((p) => p.title);
    setTitles && setTitles(titles);
  }, [programsList, setTitles]);

  const handleClickCard = () => {
    doSetStorageData({
      scrollY: window.scrollY,
    });
  };

  if (isLoading) {
    return (
      <div className={st.loader}>
        <SkeletonList />
      </div>
    );
  }

  if (programsList.length > 0) {
    return (
      <>
        <section className={st.list}>
          <InfinityList
            hasMore={hasNextPage}
            loadMore={fetchNextPage}
            loading={isFetchingNextPage}
            className={st.scroller}
          >
            {programsList.map((program, index) => (
              <ProgramCard
                activeTab={tabName}
                needDropDawnShowUp={programsList.length - 1 === index && programsList.length > 6}
                key={program.pk}
                program={program}
                onClick={handleClickCard}
              />
            ))}
          </InfinityList>
        </section>
      </>
    );
  }

  if (programsList.length === 0) {
    return (
      <Blank
        text={
          params.search || params.tags?.length
            ? 'По вашему запросу ничего не найдено'
            : 'В этом списке ничего нет'
        }
      />
    );
  }

  if (isError) {
    return <Blank text="Возникли технические неполадки, обратитесь в поддержку" />;
  }
};
